/**
 * Authors: mariushirjanu
 * Created: 21/01/2025
 */
import _ from 'underscore';
import { util } from '@app/shared/util';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { StateService } from '@uirouter/angular';
import { GaWizard } from '@app/shared/wizard/ga-wizard';
import { GaWizardService } from '@app/shared/wizard/ga-wizard.service';
import { FormlyInputType, GaFormlyService, IExtraFieldConfigurationObject } from '@app/shared/formly/ga-formly.service';
import { UntypedFormGroup } from '@angular/forms';
import { ACSL_PERMISSION, FaqDataService } from '@app/shared/faq/faq-data.service';
import { ICodeValue, IFaq } from '@type_definitions/datatypes';
import { FormattingService } from '@app/shared/services/formatting.service';
import { LOV_FAQ_CATEGORY, LOV_FAQ_SUBJECT, LOV_TRAINING_LEVEL, LOV_WEBSHOP_TYPE, LovService } from '@app/shared/services/lov.service';
import { CompanyDataService } from '@app/shared/company/company-data.service';
import { GaValidateService } from '@app/shared/formly/ga-validate.service';
import { STATE_FAQ_EDIT } from '@app/shared/app-states';
import { AuthService } from '@app/shared/services/auth/auth.service';
import { GaEventService, IGAEvent } from '@app/shared/services/ga-event.service';
import { EVENT_SAVE_FAQ } from '@app/shared/app-events';
import { Subscription } from 'rxjs';
import { EDITOR_TINYMCE, EditorConfigService, TINYMCE_ADVANCED } from '@app/shared/editor/editor-config.service';

export const WIZARD_FAQ:string = 'faq';
const ACTION_SAVE:string = 'SAVE';

@Component({
  selector: 'faq-manage',
  template:
    `
      <div class="row row-cols-2" *ngIf="!loading && isACSLUser">
        <div class="col-md-12">
          <wizard-wrapper *ngIf="!showInDialog" [wizardTitle]="title" [wizardId]="wizardId" [loading]="loading"></wizard-wrapper>
          <formly [form]="form" [fields]="fields" [model]="model" [noValidate]="true"></formly>
        </div>
        <div class="col">
          <formly [form]="detailsForm" [fields]="detailsFields" [model]="model" [noValidate]="true"></formly>
        </div>

        <div class="col">
          <h4 class="form-title">Display for</h4>
          <p>
            <c-checkbox-list
              class="form-horizontal list-permissions"
              key="permissions"
              [possibleValues]="faqPossiblePermissions"
              [values]="faqPermissions"
              (onSelectValue)="updateFaqPermissions($event)"
              [formatFunction]="formatLovFn"
            ></c-checkbox-list>
          </p>
          <formly [form]="displayForForm" [fields]="displayForFields" [model]="model" [noValidate]="true"></formly>
        </div>
        
  
        <div class="col">
          <formly [form]="confluenceForm" [fields]="confluenceFields" [model]="model" [noValidate]="true"></formly>
        </div>
  
        <div class="col col-md-12">
          <h4 class="form-title">Documents</h4>
          <faq-manage-documents [model]="model"></faq-manage-documents>
        </div>
  
        <div class="col float-start">
          <h4 class="form-title">Keywords</h4>
          <c-checkbox-list
            class="list-keywords"
            key="keywords"
            [columns]="3"
            [possibleValues]="faqPossibleKeywords"
            [values]="faqKeywords"
            (onSelectValue)="updateFaqKeywords($event)"
            [formatFunction]="formatLovFn"
          ></c-checkbox-list>
        </div>
  
        <div class="col">
          <h4 class="form-title">Manual</h4>
          <c-checkbox-list
            class="list-manuals"
            key="manuals"
            [possibleValues]="faqPossibleManuals"
            [values]="faqManuals"
            [formatFunction]="formatLovFn"
          ></c-checkbox-list>
        </div>
      </div>
      <activity-indicator *ngIf="loading"></activity-indicator>
    `
})
export class FaqManageComponent implements OnInit, OnDestroy {
  @Input() public faqId:number;
  @Input() public faqCode:string;
  @Input() public create:boolean;

  // bindings for opening as dialog from browselist
  @Input() public showInDialog:boolean;
  @Output() public onSaved:EventEmitter<IFaq> = new EventEmitter();
  @Output() public onSaving:EventEmitter<boolean> = new EventEmitter();

  public wizardId:string = WIZARD_FAQ;
  public title:string;
  public loading:boolean;
  public isACSLUser:boolean;

  private wizard:GaWizard;
  private eventHandler:Subscription;
  private uploadCompletedEventListener:Subscription;
  private trainingLevelField:IExtraFieldConfigurationObject;
  private categoryField:IExtraFieldConfigurationObject;
  private subjectField:IExtraFieldConfigurationObject;
  private vendorField:IExtraFieldConfigurationObject;
  private webshopTypeField:IExtraFieldConfigurationObject;
  private uploadedDocumentIds?:number[];

  public form:UntypedFormGroup = new UntypedFormGroup({});
  public detailsForm:UntypedFormGroup = new UntypedFormGroup({});
  public displayForForm:UntypedFormGroup = new UntypedFormGroup({});
  public confluenceForm:UntypedFormGroup = new UntypedFormGroup({});
  public fields:IExtraFieldConfigurationObject[];
  public detailsFields:IExtraFieldConfigurationObject[];
  public displayForFields:IExtraFieldConfigurationObject[];
  public confluenceFields:IExtraFieldConfigurationObject[];
  public model:IFaq = <any>{};
  public codeBackup:string;

  public faqPossiblePermissions:ICodeValue[] = [];
  public faqPossibleManuals:ICodeValue[] = [];
  public faqPossibleKeywords:ICodeValue[] = [];
  public faqPermissions:ICodeValue[] = [];
  public faqManuals:ICodeValue[] = [];
  public faqKeywords:ICodeValue[] = [];

  public formatLovFn:Function = this.formattingService.formatLov;

  constructor(
    private authService:AuthService,
    private faqDataService:FaqDataService,
    private wizardService:GaWizardService,
    private formlyService:GaFormlyService,
    private formattingService:FormattingService,
    private lovService:LovService,
    private companyDataService:CompanyDataService,
    private validateService:GaValidateService,
    private stateService:StateService,
    private eventService:GaEventService,
    private editorConfigService:EditorConfigService
  ) {}

  public ngOnInit():void {
    this.isACSLUser = this.authService.checkAcslUser();
    if (!this.isACSLUser) {
      return;
    }

    this.title = this.stateService.current.data?.viewTitle;
    this.wizard = this.wizardService.getInstance(WIZARD_FAQ);
    this.faqPossiblePermissions = this.faqDataService.getFaqPossiblePermissions();
    this.initWizard();
    this.createFields();
    this.fillFields();
    this.loadData();
    this.listen();
  }

  private listen():void {
    if(this.showInDialog) {
      this.eventHandler = this.eventService.events.subscribe((event:IGAEvent) => {
        if (event.name === EVENT_SAVE_FAQ) {
          this.doSave();
        }
      });
    }

    // this collects a list of all uploaded documents and link them to the ticket at the moment of creation
    this.uploadCompletedEventListener = this.editorConfigService.onUploadCompleted.subscribe((uploadedDocumentIds) => {
      this.uploadedDocumentIds = _.uniq((this.uploadedDocumentIds || []).concat(uploadedDocumentIds));
    });
  }

  private initWizard():void {
    this.wizard.actions([
      this.wizardService.createActionButton(
        ACTION_SAVE,
        'Save FAQ',
        'btn btn-success',
        this.doSave
      )
    ]);
  }

  private createFields():void {
    const fullLabelClass:string = 'col-lg-1 col-md-2';
    const fullValueClass:string = 'col-lg-11 col-md-10';
    const halfLabelClass:string = 'col-md-3 col-sm-2';
    const halfValueClass:string = 'col-md-9 col-sm-10';
    const codeInfoText:string = 'The code is used to unique identify the FAQ. Code can be only one time set and cannot be changed later.';

    this.fields = [
      this.formlyService.aGroup('id', 'ID')
        .withLabelClass(fullLabelClass)
        .withValueClass(fullValueClass)
        .withFields([
          this.formlyService.aLink(`${this.faqId}`)
            .withWrapperClass('col-form-label')
            .withExtraOptions({
              templateOptions: {
                href: `#/faq/${this.faqId}`,
                target: '_blank'
              }
            })
            .withLabelClass('d-none')
            .build()
        ])
        .withHideExpression(() => !!this.create)
        .build(),

      this.formlyService.anInput('code', 'Code')
        .isHide()
        .withHideExpression(() => !!this.codeBackup)
        .withLabelClass(fullLabelClass)
        .withValueClass('col-lg-3 col-md-4')
        .withInfo(codeInfoText)
        .withoutInfoOffset()
        .withPlaceholder()
        .build(),

      this.formlyService.aValue('code', 'Code')
        .isHide()
        .withHideExpression(() => !this.codeBackup)
        .withLabelClass(fullLabelClass)
        .withValueClass('col-lg-3 col-md-4')
        .withInfo(codeInfoText)
        .withoutInfoOffset()
        .build(),

      this.formlyService.aTextarea('question', 'Question')
        .isRequired()
        .withLabelClass(fullLabelClass)
        .withValueClass(fullValueClass)
        .withExtraOptions({
          templateOptions: {
            rows: 2
          }
        })
        .withPlaceholder()
        .build(),

      this.formlyService.aTextarea('answer', 'Answer')
        .isRequired()
        .withLabelClass(fullLabelClass)
        .withValueClass(fullValueClass)
        .withExtraOptions({
          templateOptions: {
            editor: EDITOR_TINYMCE,
            editorCode: TINYMCE_ADVANCED
          }
        })
        .withPlaceholder()
        .build(),

      this.formlyService.aTextarea('comments', 'Comment')
        .withLabelClass(fullLabelClass)
        .withValueClass(fullValueClass)
        .withExtraOptions({
          templateOptions: {
            rows: 2
          }
        })
        .withPlaceholder()
        .build()
    ];

    this.detailsFields = [
      this.formlyService.aTitle('Details')
        .build(),

      this.formlyService.anInput('sequence', 'Sequence')
        .withLabelClass(halfLabelClass)
        .withValueClass(halfValueClass)
        .withInputType(FormlyInputType.NUMBER)
        .build(),

      this.categoryField = this.formlyService.aSelect('category', 'Category')
        .withLabelClass(halfLabelClass)
        .withValueClass(halfValueClass)
        .withFormat(this.formattingService.formatLov)
        .build(),

      this.trainingLevelField = this.formlyService.aSelect('trainingLevel', 'Training Level')
        .withLabelClass(halfLabelClass)
        .withValueClass(halfValueClass)
        .withFormat(this.formattingService.formatLov)
        .build(),

      this.subjectField = this.formlyService.aSelect('subject', 'Subject')
        .withLabelClass(halfLabelClass)
        .withValueClass(halfValueClass)
        .withFormat(this.formattingService.formatLov)
        .build()
    ];

    this.displayForFields = [
      this.formlyService.aSearch('company', 'Company')
        .withLabelClass(halfLabelClass)
        .withValueClass(halfValueClass)
        .withFormat(this.formattingService.formatCompany)
        .withExtraOptions({
          templateOptions: {
            searchFunction: {
              request: (filter:string) => this.companyDataService.searchCompanies(filter)
            }
          }
        })
        .build(),

      this.vendorField = this.formlyService.aSelect('vendor', 'Vendor')
        .withLabelClass(halfLabelClass)
        .withValueClass(halfValueClass)
        .withFormat(this.formattingService.formatCompany)
        .build(),

      this.webshopTypeField = this.formlyService.aSelect('projectType', 'Webshop Type')
        .withLabelClass(halfLabelClass)
        .withValueClass(halfValueClass)
        .withFormat(this.formattingService.formatLov)
        .build()
    ];

    this.confluenceFields = [
      this.formlyService.aTitle('Confluence')
        .build(),

      this.formlyService.anInput('confluenceSpaceKey', 'Space')
        .withLabelClass(halfLabelClass)
        .withValueClass(halfValueClass)
        .build(),

      this.formlyService.anInput('confluencePageTitle', 'Page Title')
        .withLabelClass(halfLabelClass)
        .withValueClass(halfValueClass)
        .build()
    ];
  }

  private fillFields():void {
    this.formlyService.fillField(this.trainingLevelField, this.lovService.list(LOV_TRAINING_LEVEL));
    this.formlyService.fillField(this.subjectField, this.lovService.list(LOV_FAQ_SUBJECT));
    this.formlyService.fillField(this.categoryField, this.lovService.list(LOV_FAQ_CATEGORY));
    this.formlyService.fillField(this.webshopTypeField, this.lovService.list(LOV_WEBSHOP_TYPE));
    this.formlyService.fillField(this.vendorField, this.companyDataService.getVendorCompanies());

    this.faqDataService.getFaqManuals()
      .then((manuals:ICodeValue[]) => {
        this.faqPossibleManuals = manuals;
      });

    this.faqDataService.getFaqKeywords()
      .then((keywords:ICodeValue[]) => {
        this.faqPossibleKeywords = keywords;
      });
  }

  private loadData():void {
    //  prevent loading the faq when creating a new faq using an existing code
    if (this.faqCode || (!this.create && this.faqId)) {
      this.loading = true;
      const loadFaqPromise:Promise<IFaq> = this.create || this.faqCode? this.faqDataService.getFaqByCode(this.faqCode) : this.faqDataService.getFaq(this.faqId);
      loadFaqPromise
        .then((faq:IFaq) => {
          if (!this.create) {
            // when editing an
            this.model = faq;
            this.codeBackup = faq.code;
            this.setInitialPermissions();
            this.faqManuals = faq.manuals;
            this.faqKeywords = faq.keywords;
          } else if (faq.id) {
            // redirect to the edit page if trying to create a new FAQ with an existing code
            this.stateService.go(STATE_FAQ_EDIT, { key: faq.id });
          }
        })
        .catch(util.noop)
        .finally(() => {
          this.loading = false;
        });

      // fill in the code when creating a new FAQ
      if(this.create && this.faqCode){
        this.model.code = this.faqCode;
        this.codeBackup = this.faqCode;
      }
    }

    // Display for ACSL will be checked by default when creating a new FAQ
    if(this.create){
      this.model[ACSL_PERMISSION] = true;
      this.setInitialPermissions();
    }
  }
  
  private validate():Promise<void|string[]> {
    return this.validateService.validateFields(
      this.fields.concat(
        this.detailsFields,
        this.displayForFields,
        this.confluenceFields
      ),
      this.model,
      true,
      undefined,
      true
    );
  }

  private doSave = ():void => {
    this.validate()
      .then(() => {
        this.addEditorDocumentIdsToModel();
        this.wizard.updateActionStatus(ACTION_SAVE, true);
        this.onSaving.emit(true);
        return this.model.id ? this.faqDataService.updateFaq(this.model.id, this.model) : this.faqDataService.createFaq(this.model);
      })
      .then((faq:IFaq) => {
        if(this.create && !this.showInDialog) {
          // redirect to FAQ edit screen
          this.stateService.go(STATE_FAQ_EDIT, { key:  faq.id });
        } else {
          this.model = faq;
          this.codeBackup = faq.code;
        }
        this.onSaved.emit(faq);
      })
      .catch(util.noop)
      .finally(() => {
        this.uploadedDocumentIds = [];
        this.wizard.updateActionStatus(ACTION_SAVE, false);
        this.onSaving.emit(false);
      });
  };

  private setInitialPermissions():void {
    const faqPermissions:ICodeValue[] = [];
    _.each(this.faqPossiblePermissions, (permission:ICodeValue) => {
      if(this.model[permission.id]){
        faqPermissions.push(permission);
      }
    });
    this.faqPermissions = faqPermissions;
  }

  private addEditorDocumentIdsToModel():void {
    // add document ids to the model in order to make sure that the uploaded files are still present in the content of the editor
    const contentDocumentIds:number[] = this.editorConfigService.extractDocumentIdsFromContent(this.model.answer);
    if (contentDocumentIds.length) {
      const uploadedDocumentIds:number[] = _.intersection(this.uploadedDocumentIds, contentDocumentIds);
      this.model.uploadedDocumentIds = uploadedDocumentIds?.length ? uploadedDocumentIds : undefined;
    }
  }

  public updateFaqPermissions(permissions:ICodeValue[]):void {
    _.each(this.faqPossiblePermissions, (possiblePermission:ICodeValue) => {
      this.model[possiblePermission.id] = _.findIndex(permissions, (permission:ICodeValue) => permission.id === possiblePermission.id) !== -1;
    });
  }

  public updateFaqKeywords(keywords:ICodeValue[]):void {
    this.model.keywords = keywords;
  }

  public ngOnDestroy():void {
    this.wizard?.clear();
    this.showInDialog && this.eventHandler?.unsubscribe();
    this.uploadCompletedEventListener?.unsubscribe();
  }
}
